import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import { BroadcastService } from '@services/broadcast-service';
import {EventLoggerService} from '@services/event-logger-service';

interface VariantData {
  quantityUnit: string;
  objectId: string;
  cartQuantity: number;
  margUnit: number;
  quantity?: number;
  perBottle?: number;
  mrp: number;
  price: number;
  offPercentage: number;
}

interface VariantsPopupBannerData {
  title: string;
  productImage: string;
  variantData: VariantData[];
  unit?: string;
}

interface VariantsPopupData {
  bannerData: VariantsPopupBannerData;
  mainProductId: string;
  section?: string;
  experimentVariantData?: {
    variant?: {
      uiVariants?: {
        showProductPopup?: boolean;
      };
    };
  };
  callbacks: {
    closeBottomSheet: () => void;
    addOrRemoveProductToCart: (productId: string, action: 'ADD' | 'REMOVE') => Promise<void>;
    handleProceedToBuy: (productTitle: string) => void;
  };
}

@Component({
  selector: 'variants-popup',
  templateUrl: './variants-popup.html',
  imports: [
    NgForOf,
    NgIf,
    NgClass,
  ],
  standalone: true
})
export class VariantsPopupComponent implements OnInit {
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: VariantsPopupData,
    private readonly broadcast: BroadcastService,
    public readonly eventLogger: EventLoggerService,
    private readonly bottomSheetRef: MatBottomSheetRef<VariantsPopupComponent>,
    private readonly cdr: ChangeDetectorRef
  ) {
    // Prevent default dismissal behavior
    this.bottomSheetRef.disableClose = true;

    // Handle backdrop clicks
    this.bottomSheetRef.backdropClick().subscribe(() => {
      this.closeSheet();
    });
  }

  showProceedToBuy: boolean = false;
  activeMargUnit: number = 0;

  ngOnInit(): void {
    this.showProceedToBuy = this.data?.bannerData?.variantData?.some((d) => d?.cartQuantity) || false;
    this.eventLogger.cleverTapEvent('impression', JSON.stringify({
      name: 'add_to_cart_variant_selection_bs',
      main_productId: String(this.data?.mainProductId),
      section: this.data?.section ?? 'unknown',
    }));
  }

  async handleQuantityChange(variant: VariantData, action: 'ADD' | 'REMOVE'): Promise<void> {
    try {
      if (!variant) return;

      const isAdd = action === 'ADD';
      const currentQuantity = variant.cartQuantity || 0;

      // Check quantity limits
      if (isAdd && currentQuantity >= 5) return this.broadcast.broadcast('NOTIFY', { message: 'Maximum quantity is limited to 5 per product' });
      if (!isAdd && currentQuantity <= 0) return;

      // Call parent method through callbacks
      await this.data.callbacks.addOrRemoveProductToCart(variant.objectId, action);

      // Only update local state if API call succeeds
      variant.cartQuantity = isAdd ? currentQuantity + 1 : currentQuantity - 1;

      this.eventLogger.cleverTapEvent('click', JSON.stringify({
        name: isAdd ? 'variant_bs_add_to_cart' : 'variant_bs_remove_from_cart',
        main_productId: String(this.data.mainProductId),
        selected_variant: `${variant.quantity}_${variant.quantityUnit}_${variant.margUnit}`,
        productId: variant.objectId,
        section: this.data.section ?? 'unknown',
      }));

      // Update showProceedToBuy based on any variant having cartQuantity
      this.showProceedToBuy = this.data?.bannerData?.variantData?.some((d) => d?.cartQuantity) || false;

      // Trigger change detection to update UI immediately
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error updating cart:', error);
      // Do not update local state if API call fails
    }
  }

  setActiveMargUnit(activeMargUnit: number): void {
    this.activeMargUnit = activeMargUnit;
    if (activeMargUnit > 0) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'variants_popup_product_image', margId: activeMargUnit }));
    }
  }

  closeSheet(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'variant_bs_closed' }));
    this.setActiveMargUnit(0);
    this.data.callbacks.closeBottomSheet();
  }
}
